import React, { useState } from 'react';
import { toast } from 'sonner';
import { Link2, Loader2, MonitorPlay, Smartphone, Trash2, Youtube } from 'lucide-react';
import api, { errMsg, fileUrl } from '@/lib/api';
import { ImageDrop } from '@/components/ImageDrop';
import { useI18n } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export const Step1Reference = ({ reference, setReference, coverType, setCoverType }) => {
  const { t } = useI18n();
  const [url, setUrl] = useState('');
  const [busyUpload, setBusyUpload] = useState(false);
  const [busyFetch, setBusyFetch] = useState(false);

  const COVER_TYPES = [
    { id: 'youtube', label: 'YouTube', hint: t('step1.youtubeHint'), icon: MonitorPlay },
    { id: 'shorts', label: 'YT Shorts', hint: t('step1.shortsHint'), icon: Smartphone },
  ];

  const upload = async (files) => {
    setBusyUpload(true);
    try {
      const fd = new FormData();
      fd.append('file', files[0]);
      const { data } = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setReference({
        file_id: data.file_id,
        url: data.url,
        source: 'upload',
        width: data.width,
        height: data.height,
        message: t('step1.refUploaded'),
      });
      toast.success(t('step1.refUploadedToast'));
    } catch (e) {
      toast.error(errMsg(e, t('step1.uploadFailed')));
    } finally {
      setBusyUpload(false);
    }
  };

  const fetchYoutube = async () => {
    if (!url.trim()) {
      toast.error(t('step1.pasteFirst'));
      return;
    }
    setBusyFetch(true);
    try {
      const { data } = await api.post('/studio/youtube', { url: url.trim() });
      setReference({
        file_id: data.file_id,
        url: data.url,
        source: 'youtube',
        title: data.title,
        width: data.width,
        height: data.height,
        message: data.message,
        video_id: data.video_id,
      });
      toast.success(t('step1.fetched'));
    } catch (e) {
      toast.error(errMsg(e, t('step1.fetchFailed')), { duration: 7000 });
    } finally {
      setBusyFetch(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Cover type */}
      <div>
        <h2 className="font-display text-lg font-semibold">{t('step1.coverQuestion')}</h2>
        <p className="mt-1.5 text-sm text-[hsl(var(--text-muted))]">{t('step1.coverDesc')}</p>
        <div data-testid="cover-type-selector" className="mt-4 grid gap-3 sm:grid-cols-2">
          {COVER_TYPES.map((c) => {
            const sel = coverType === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`cover-type-${c.id}`}
                onClick={() => setCoverType(c.id)}
                className={[
                  'flex items-center gap-3.5 rounded-[var(--radius-md)] border p-4 text-left transition-colors duration-150',
                  sel
                    ? 'border-[hsl(var(--brand-red))] bg-[hsl(var(--surface-2))] ring-1 ring-[hsl(var(--brand-red))]/40'
                    : 'border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] hover:border-[hsl(var(--border-strong))]',
                ].join(' ')}
              >
                <span
                  className={[
                    'grid h-12 w-12 shrink-0 place-items-center rounded-[12px]',
                    sel ? 'bg-[hsl(var(--brand-red))] text-white' : 'bg-[hsl(var(--surface-2))] text-[hsl(var(--brand-red))]',
                  ].join(' ')}
                >
                  <c.icon className="h-6 w-6" strokeWidth={1.6} />
                </span>
                <span>
                  <span className="block text-sm font-semibold">{c.label}</span>
                  <span className="mt-0.5 block font-mono-alt text-[11px] text-[hsl(var(--text-muted))]">
                    {c.hint}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-[hsl(var(--border-soft))]" />

      <div>
        <h2 className="font-display text-lg font-semibold">{t('step1.refTitle')}</h2>
        <p className="mt-1.5 text-sm text-[hsl(var(--text-muted))]">{t('step1.refDesc')}</p>
      </div>

      {reference ? (
        <div className="rounded-[var(--radius-lg)] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] p-3">
          <div className="flex gap-3">
            <img
              src={fileUrl(reference.url)}
              alt={t('step1.refTitle')}
              data-testid="reference-preview-image"
              className="h-20 w-32 shrink-0 rounded-[10px] border border-[hsl(var(--border-soft))] object-cover"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[hsl(var(--surface))] px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--text-2))]">
                  {reference.source === 'youtube' ? t('step1.sourceYoutube') : t('step1.sourceUpload')}
                </span>
                <span className="font-mono-alt text-[11px] text-[hsl(var(--text-muted))]">
                  {reference.width}x{reference.height}
                </span>
              </div>
              {reference.title && (
                <p className="mt-1.5 truncate text-sm font-medium text-white">{reference.title}</p>
              )}
              <p className="mt-1 text-xs text-[hsl(var(--text-muted))]">{reference.message}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              data-testid="reference-clear-button"
              onClick={() => setReference(null)}
              className="h-8 w-8 shrink-0 text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--surface))] hover:text-[hsl(var(--brand-red))]"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <ImageDrop
          testId="reference-dropzone"
          onFiles={upload}
          busy={busyUpload}
          title={t('step1.dropTitle')}
        />
      )}

      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-[hsl(var(--border-soft))]" />
        <span className="text-xs font-semibold text-[hsl(var(--text-muted))]">{t('step1.orYoutube')}</span>
        <span className="h-px flex-1 bg-[hsl(var(--border-soft))]" />
      </div>

      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Youtube className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--brand-red))]" />
            <Input
              data-testid="youtube-link-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') fetchYoutube();
              }}
              placeholder="https://www.youtube.com/watch?v=..."
              className="bg-[hsl(var(--surface-2))] pl-9"
            />
          </div>
          <Button
            onClick={fetchYoutube}
            disabled={busyFetch}
            data-testid="youtube-link-fetch-button"
            className="h-10 bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
          >
            {busyFetch ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="mr-2 h-4 w-4" /> {t('step1.fetchCover')}</>}
          </Button>
        </div>
        <p className="text-xs text-[hsl(var(--text-muted))]">{t('step1.youtubeNote')}</p>
      </div>
    </div>
  );
};
